'use client';

/* eslint-disable @typescript-eslint/no-unused-vars */
import gsap from 'gsap';
import { useRef } from 'react';
import styles from './ExperienceNew.module.css';
import { SPEEDSIZE_CDN } from 'lib/config/assetUrls';
import { drawImageSafe } from 'utils/drawImageSafe';
import { useIsomorphicLayoutEffect } from 'hooks/dom/useIsomorphicLayoutEffect';
import { useGsapContext } from '../../hooks/animation/useGsapContext';
import { useMasterTimeline } from 'hooks/animation/useMasterTimeline';
import { SectionHeader } from './v2/SectionHeader';

// Helper to draw image bottom-aligned and scaled to fit canvas
function drawImageBottomAligned(
  context: CanvasRenderingContext2D,
  img: HTMLImageElement,
  canvas: HTMLCanvasElement,
) {
  const scale = canvas.width / img.width;
  const drawWidth = canvas.width;
  const drawHeight = img.height * scale;
  const yOffset = canvas.height - drawHeight;
  context.clearRect(0, 0, canvas.width, canvas.height);
  drawImageSafe(
    context,
    img,
    0,
    yOffset > 0 ? yOffset : 0,
    drawWidth,
    drawHeight,
  );
}

export const Experience = ({ className }: { className?: string }) => {
  const sectionRoot = useRef<HTMLDivElement>(null);
  const container = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const canvas = useRef<HTMLCanvasElement>(null);
  const gsapContext = useGsapContext();
  const { add } = useMasterTimeline();
  const intersectionObserver = useRef<IntersectionObserver | null>(null);
  const animationTriggered = useRef<boolean>(false);

  const resolutionMap = {
    sm: {
      tag: 'w_800',
      width: 800,
      height: 1422,
      frameStep: 1,
    },
    lg: {
      tag: 'w_1920',
      width: 1920,
      height: 1080,
      frameStep: 1,
    },
  };

  useIsomorphicLayoutEffect(() => {
    if (typeof window === 'undefined') return;
    if (
      !sectionRoot.current ||
      !container.current ||
      !canvas.current ||
      !contentRef.current
    )
      return;
    const canvasEl = canvas.current;
    const context = canvasEl.getContext('2d', { desynchronized: true });

    // Define screenSize at the top scope of the effect
    const screenSize: 'sm' | 'lg' = window.innerWidth > 767 ? 'lg' : 'sm';
    canvasEl.width = resolutionMap[screenSize].width;
    canvasEl.height = resolutionMap[screenSize].height;

    const frameCount = 119;

    // Set initial opacity with GSAP
    gsap.set(contentRef.current, { opacity: 0, y: 20 });

    intersectionObserver.current = new IntersectionObserver(
      (e) => {
        if (e[0].intersectionRatio <= 0) return;

        const targetRect = e[0].boundingClientRect;
        const rootRect = e[0].rootBounds;

        const currentFrame = (index: number) =>
          `${SPEEDSIZE_CDN}/${
            process.env.NEXT_PUBLIC_SPEEDSIZE_CLIENT_ID
          }/public-web-assets.uh-static.com/web_v2/sequence/exploding-home-2/${screenSize}/${index
            .toString()
            .padStart(3, '0')}.png/${resolutionMap[screenSize].tag}`;

        if (context && rootRect) {
          const img = new Image();
          if (targetRect.bottom < rootRect.top) {
            img.src = currentFrame(0);
          } else if (targetRect.top > rootRect.bottom) {
            img.src = currentFrame(frameCount);
          }

          img.onload = () => {
            drawImageBottomAligned(context, img, canvasEl);
          };
        }

        if (animationTriggered.current) return;
        animationTriggered.current = true;

        const images: HTMLImageElement[] = [];
        const totalFrames = Math.floor(
          frameCount / resolutionMap[screenSize].frameStep,
        );
        let allImagesLoaded = false;
        let loadedCount = 0;
        // Preload all images before starting animation
        for (
          let i = 1;
          i < frameCount;
          i += resolutionMap[screenSize].frameStep
        ) {
          const img = new Image();
          img.crossOrigin = 'anonymous';
          img.src = currentFrame(i);
          img.onload = () => {
            loadedCount++;
            if (loadedCount === totalFrames - 1) {
              allImagesLoaded = true;
              startGsapTimeline();
            }
          };
          images.push(img);
        }
        images.reverse();

        function startGsapTimeline() {
          gsapContext.add(() => {
            const tl = gsap.timeline({
              defaults: { duration: 1 },
              scrollTrigger: {
                immediateRender: false,
                trigger: container.current,
                start: 'top top',
                end: 'bottom bottom',
                scrub: 0.1,
                invalidateOnRefresh: true,
                markers: false,
              },
            });

            const ring = {
              frame: 0,
              ease: 0,
            };

            const render = (input?: number) => {
              const img = input
                ? images[images.length - 1]
                : images[ring.frame];
              if (!img || !img.complete || img.naturalWidth === 0) {
                return;
              }
              if (!context) return;
              requestAnimationFrame(() => {
                drawImageBottomAligned(context, img, canvasEl);
              });
            };

            tl.fromTo(
              ring,
              {
                frame: images.length - 1,
              },
              {
                frame: 0,
                snap: { frame: 1 },
                ease: 'power1.inOut',
                onUpdate: render,
                onComplete: () => render(),
              },
            );

            // Background color transition
            // tl.fromTo(
            //   container.current,
            //   {
            //     backgroundColor: '#ffffff',
            //   },
            //   {
            //     backgroundColor: '#000000',
            //     duration: 0.4, // Increased duration for smoother transition
            //     ease: 'power2.inOut', // Smoother easing
            //   },
            //   '0.21',
            // );

            // Calculate animation progress points
            const fadeInPoint = 0.55;
            const fadeOutPoint = 0.85;

            // Content opacity with improved easing
            tl.to(
              contentRef.current,
              {
                opacity: 1,
                y: 0,
                duration: 0.5, // Increased duration
                ease: 'power2.out',
              },
              fadeInPoint,
            );

            tl.to(
              contentRef.current,
              {
                opacity: 0,
                y: -20,
                duration: 0.5, // Increased duration
                ease: 'power2.in',
              },
              fadeOutPoint,
            );

            tl.pause();

            // Register with the MasterTimeline for orchestration/debug overlay
            try {
              add(tl, 'experience-new', 4);
            } catch (e) {
              // no-op
            }
          });
        }
      },
      {
        root: null,
        rootMargin: '1000px', // Increased margin for earlier loading
        threshold: 0.01,
      },
    );

    intersectionObserver.current?.observe(sectionRoot.current);

    return () => {
      intersectionObserver.current?.disconnect();
    };
  }, []);

  return (
    <section
      className={className ? `${styles.root} ${className}` : styles.root}
      ref={sectionRoot}
    >
      <div className="sticky-wrapper" ref={container}>
        <div className="container">
          <div className="content" ref={contentRef}>
            <SectionHeader
              title="Built with precision"
              subtitle="What’s inside is built to understand what’s around you, enabling a more balanced and restful environment."
              variant="dark"
              marginBottom="0"
            />
          </div>
        </div>
        <div className="sticky">
          <canvas ref={canvas}></canvas>
        </div>
      </div>
    </section>
  );
};
